import Skeleton from "react-loading-skeleton";
import { Wrapper } from "../components/layout/wrapper";
import { NewsEntry, useNewsContext } from "../contexts/news";
import RatioImage from "../components/medias/ratio-image";
import { useLocation, useSearchParams } from "react-router";
import SpriteIcon from "../components/medias/sprite-icon";
import { defineMessages, useIntl } from "react-intl";
import Button from "../components/common/button";
import { useAppConfig } from "@ct-react/core";
import { useLocaleContext, useLocaleUrl } from "@ct-react/locale";
import SeoMetas from "../components/layout/seo-metas";
import NotFoundPage from "./not-found";

import "./single-new.scss";

const transDefs = defineMessages({
  liked_the_post: {id: "single-new.like-the-post", defaultMessage: "Ce post vous a plu ?"},
  share_it: {id: "single-new.share-it", defaultMessage: "Partagez-le !"},
  seo_fallback_description: {
    id: "single-new.seo.fallback-description",
    defaultMessage: "{title} : retrouvez cette actualité et les informations d’Inter-Agence à Nendaz."
  }
});

const SingleNew = () => {
  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { pathname } = useLocation();
  const { loading, newsContent } = useNewsContext();
  const [ searchParams ] = useSearchParams();
  const { canonicalBaseUrl } = useAppConfig();
  const { buildLocaleHref } = useLocaleUrl(canonicalBaseUrl);
  const id = searchParams.get('refId');
  const currentNew = newsContent?.find((n: NewsEntry) => n.id === id);

  const localePrefix = `/${locale.basename}`;
  const routePath = pathname.startsWith(`${localePrefix}/`)
    ? pathname.slice(localePrefix.length)
    : pathname;
  const canonicalPath = id
    ? `${routePath}?refId=${encodeURIComponent(id)}`
    : routePath;
  const encodedArticleUrl = encodeURIComponent(buildLocaleHref(canonicalPath, locale));

  if (!id) return <NotFoundPage />;

  if (loading) {
    return (
      <>
        <SeoMetas canonical={canonicalPath}/>
        <Wrapper.TwoCol as="section"
                        className="single-new reverse">
          <Wrapper.ColText>
            <h1><Skeleton/></h1>
            <span><Skeleton/></span>
            <p><Skeleton count={4}/></p>
            <hr></hr>
            <p><Skeleton count={15}/></p>
          </Wrapper.ColText>
          <Wrapper.ColImage>
            <picture className="rimg skeleton"><Skeleton height="100%"/></picture>
          </Wrapper.ColImage>
        </Wrapper.TwoCol>
      </>
    );
  }

  if (!newsContent || !currentNew) {
    return <NotFoundPage />;
  }

  const seo = currentNew.data["seo-data"];
  const articleTitle = seo?.["seo-title"] || currentNew.data["news-title"];
  const articleDescription = seo?.["seo-description"]
    || currentNew.data["news-lead"]
    || intl.formatMessage(transDefs.seo_fallback_description, { title: currentNew.data["news-title"] });
  const articleImage = seo?.["seo-image"]?.originalUrl || currentNew.data["news-image"]?.originalUrl;
  const keywords = typeof seo?.["seo-keywords"] === "string"
    ? seo["seo-keywords"].split(",").map((keyword: string) => keyword.trim()).filter(Boolean)
    : undefined;

  return (
    <>
      <SeoMetas title={articleTitle}
                description={articleDescription}
                image={articleImage}
                keywords={keywords}
                canonical={canonicalPath}/>
      <Wrapper.TwoCol as="article"
                      className="single-new">
        <Wrapper.ColText>
          <RatioImage source={currentNew.data["news-image"].assets}
                      alt={currentNew.data["news-image"].alternativeText || currentNew.data["news-title"]}/>
        </Wrapper.ColText>
        <Wrapper.ColImage>
          <header>
            <h1>{currentNew.data["news-title"]}</h1>
            <span className="single-new-date">
              <SpriteIcon hash="booking.calendar"/>
              {intl.formatDate(new Date(currentNew.publishedAt), { year: "numeric", month: "long", day: "numeric" })}
            </span>
            {currentNew.data["news-lead"] && <p>{currentNew.data["news-lead"]}</p>}
            <hr></hr>
          </header>
          <div className="desc" dangerouslySetInnerHTML={{ __html: currentNew.data["news-body"] }} />
          <footer className="single-new-footer">
            <h3>
              {intl.formatMessage(transDefs.liked_the_post)}<br/>
              {intl.formatMessage(transDefs.share_it)}
            </h3>
            <div className="icones">
                <a  href={`mailto:?subject=Hey! Je t'invite à lire ce post de Montanagence&body=${encodedArticleUrl}`}
                    className="btn small with-icon outline"
                    target="_blank"
                    aria-label="Partager par email">
                    <SpriteIcon hash="contact.email"/>
                </a>
                <a  href={`https://wa.me/?text=${encodedArticleUrl}`}
                    className="btn small with-icon outline"
                    target="_blank"
                    aria-label="Partager sur Whatsapp">
                    <SpriteIcon hash="social.whatsapp"/>
                </a>
                <a  href={`https://www.facebook.com/sharer/sharer.php?u=${encodedArticleUrl}`}
                    className="btn small with-icon outline" aria-label="Copier le lien sur Facebook">
                    <SpriteIcon hash="social.facebook"/>
                </a>
                <Button className=" small with-icon outline"
                        onClick={() => window.print()}>
                  <SpriteIcon hash="action.print"/>
                </Button>
            </div>
          </footer>
        </Wrapper.ColImage>
      </Wrapper.TwoCol>
    </>
  );
};

export default SingleNew;
